import { PitchCountRules, RestThreshold } from '../types/pitch-rules';

export interface PitchingLogEntry {
  gameId: string;
  tournamentId?: string;
  date: string;              // ISO date string
  pitchCount: number;
  requiredRestDays: number;
  eligibleDate: string;      // ISO date: first date they can pitch again
  inningsPitched?: number;   // USSSA: one pitch in an inning = 1 full inning credit
  outsPitched?: number;      // Perfect Game: raw outs recorded
}

export interface PitchCountRuleset {
  id: string;
  name: string;
  description: string;
  advisory?: boolean;
  ageGroups: {
    [ageGroup: string]: Omit<PitchCountRules, 'id' | 'name'>;
  };
}

// ── Helper ───────────────────────────────────────────────────────────────────

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function dateOnly(d: Date): string {
  return d.toISOString().split('T')[0];
}

// ── Rulesets ─────────────────────────────────────────────────────────────────

export const PITCH_COUNT_RULESETS: Record<string, PitchCountRuleset> = {
  little_league: {
    id: 'little_league',
    name: 'Little League',
    description: 'Official Little League Baseball pitch count regulations. Rest is based on calendar days regardless of games played.',
    ageGroups: {
      '7U':  { logicType: 'threshold', dailyMax: 50,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }] },
      '8U':  { logicType: 'threshold', dailyMax: 50,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }] },
      '9U':  { logicType: 'threshold', dailyMax: 75,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 75, daysRestRequired: 4 }] },
      '10U': { logicType: 'threshold', dailyMax: 75,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 75, daysRestRequired: 4 }] },
      '11U': { logicType: 'threshold', dailyMax: 85,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 85, daysRestRequired: 4 }] },
      '12U': { logicType: 'threshold', dailyMax: 85,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 85, daysRestRequired: 4 }] },
      '13U': { logicType: 'threshold', dailyMax: 95,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 95, daysRestRequired: 4 }] },
      '14U': { logicType: 'threshold', dailyMax: 95,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 95, daysRestRequired: 4 }] },
      '15U': { logicType: 'threshold', dailyMax: 95,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 95, daysRestRequired: 4 }] },
      '16U': { logicType: 'threshold', dailyMax: 95,  enforceEventMax: false, restThresholds: [{ minPitches: 1, maxPitches: 20, daysRestRequired: 0 }, { minPitches: 21, maxPitches: 35, daysRestRequired: 1 }, { minPitches: 36, maxPitches: 50, daysRestRequired: 2 }, { minPitches: 51, maxPitches: 65, daysRestRequired: 3 }, { minPitches: 66, maxPitches: 95, daysRestRequired: 4 }] },
    }
  },

  usssa: {
    id: 'usssa',
    name: 'USSSA',
    description: 'USSSA innings-based limits. One pitch thrown in an inning counts as a full inning. Throwing 4+ innings in a day blocks the pitcher the following day.',
    advisory: true,
    ageGroups: {
      '7U':  { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '8U':  { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '9U':  { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '10U': { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '11U': { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '12U': { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 6, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '13U': { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 7, eventMaxInnings: 8, nextDayBlockInnings: 4 },
      '14U': { logicType: 'innings', dailyMax: 999, enforceEventMax: false, restThresholds: [], dailyMaxInnings: 7, eventMaxInnings: 8, nextDayBlockInnings: 4 },
    }
  },

  perfect_game: {
    id: 'perfect_game',
    name: 'Perfect Game',
    description: 'Outs-based daily limits with a 24-out rolling 3-day window. Both outs and pitch counts are enforced: whichever limit is hit first removes the pitcher.',
    ageGroups: {
      '7U':  { logicType: 'outs', dailyMax: 50,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 15, dailyMaxPitches: 50,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '8U':  { logicType: 'outs', dailyMax: 50,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 15, dailyMaxPitches: 50,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '9U':  { logicType: 'outs', dailyMax: 75,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 18, dailyMaxPitches: 75,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '10U': { logicType: 'outs', dailyMax: 75,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 18, dailyMaxPitches: 75,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '11U': { logicType: 'outs', dailyMax: 85,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 18, dailyMaxPitches: 85,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '12U': { logicType: 'outs', dailyMax: 85,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 18, dailyMaxPitches: 85,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '13U': { logicType: 'outs', dailyMax: 95,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 21, dailyMaxPitches: 95,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
      '14U': { logicType: 'outs', dailyMax: 95,  enforceEventMax: false, restThresholds: [], dailyMaxOuts: 21, dailyMaxPitches: 95,  rollingOutsWindowDays: 3, rollingOutsWindowMax: 24 },
    }
  },

  nfhs: {
    id: 'nfhs',
    name: 'NFHS (High School)',
    description: 'NFHS pitch count framework: 110 pitches/game maximum with threshold-based rest. Advisory rules include a 150-pitch 5-day window and no pitching on 3 consecutive calendar days.',
    advisory: true,
    ageGroups: {
      'all': {
        logicType: 'threshold',
        dailyMax: 110,
        enforceEventMax: false,
        restThresholds: [
          { minPitches: 1,   maxPitches: 30,  daysRestRequired: 0 },
          { minPitches: 31,  maxPitches: 50,  daysRestRequired: 1 },
          { minPitches: 51,  maxPitches: 70,  daysRestRequired: 2 },
          { minPitches: 71,  maxPitches: 90,  daysRestRequired: 3 },
          { minPitches: 91,  maxPitches: 110, daysRestRequired: 4 },
        ],
        rollingWindowDays: 5,
        rollingWindowMaxPitches: 150,
        noConsecutiveDays: true,
      }
    }
  },

  custom: {
    id: 'custom',
    name: 'Custom',
    description: 'Define your own pitch count limits and rest requirements.',
    ageGroups: {}
  }
};

// ── Exported helpers ─────────────────────────────────────────────────────────

// ── Custom tournament rules — single source of truth ─────────────────────────
//
// When a tournament's ruleset is 'custom', the eligibility engine needs the raw
// custom limits. Those can be defined in two places, in priority order:
//   1. ON THE TOURNAMENT (per-tournament override, set in the Create Tournament
//      dialog) — used when the tournament doc carries its own custom* fields.
//   2. ON THE TEAM (season-level defaults in Settings → Tournaments → Custom).
//
// This collapses the builder that was previously duplicated inline across
// LiveGameEngine, TournamentView, PitchingIntelPanel, and TournamentGameStrategy
// so they can never drift. Pass the tournament doc (or undefined) and the team
// settings; get back the customRules object the eligibility engine expects, or
// `settings.customRules` (the legacy season-game override) for non-custom rulesets.
//
// A source is considered "tournament-defined" if it has a customTournamentTracking
// value — the field the dialog always writes when Custom is chosen.

export function buildCustomTournamentRules(
  isCustomRuleset: boolean,
  tournament: any | undefined,
  settings: any,
): any {
  if (!isCustomRuleset) return settings?.customRules;

  // Prefer per-tournament values when the tournament defines its own custom rule.
  const fromTournament = tournament?.customTournamentTracking != null;
  const src = fromTournament ? tournament : settings;

  return {
    logicType: src?.customTournamentTracking === 'innings' ? 'innings'
             : src?.customTournamentTracking === 'outs'    ? 'outs'
             : 'threshold',
    dailyMax: src?.customTournamentDailyLimit || 85,
    eventMax: (src?.customTournamentEventLimit || 0) > 0
             ? src.customTournamentEventLimit : undefined,
    enforceEventMax: (src?.customTournamentEventLimit || 0) > 0,
    // Rest thresholds are only defined at the team level; "no rest days" clears them.
    restThresholds: src?.customTournamentNoRestDays ? [] : (settings?.restThresholds || []),
  };
}

export function calculateRestDays(pitchCount: number, thresholds: RestThreshold[]): number {
  const sorted = [...thresholds].sort((a, b) => a.maxPitches - b.maxPitches);
  for (const t of sorted) {
    if (pitchCount <= t.maxPitches) return t.daysRestRequired;
  }
  return sorted.length > 0 ? sorted[sorted.length - 1].daysRestRequired : 0;
}

/**
 * The next rest-day step up from today's count: how many more pitches until the
 * required rest grows, and to how many days. Null when no higher step exists
 * (already in the top tier, or the ruleset has no thresholds).
 */
export function nextRestStep(
  pitchesToday: number,
  thresholds: RestThreshold[],
): { pitchesUntil: number; daysRest: number } | null {
  if (!thresholds?.length) return null;
  const currentDays = pitchesToday > 0 ? calculateRestDays(pitchesToday, thresholds) : 0;
  const next = [...thresholds]
    .sort((a, b) => a.minPitches - b.minPitches)
    .find(t => t.minPitches > pitchesToday && t.daysRestRequired > currentDays);
  return next ? { pitchesUntil: next.minPitches - pitchesToday, daysRest: next.daysRestRequired } : null;
}

// ── Main eligibility function ─────────────────────────────────────────────────

export function getPitcherEligibility(
  pitchingLog: PitchingLogEntry[] = [],
  rules: PitchCountRuleset,
  ageGroup: string,
  gameDate: Date,
  currentGamePitchCount: number = 0,
  currentTournamentId?: string,
  enforceGlobalRest: boolean = true,
  customRules?: any,
  currentGameInnings: number = 0,
  currentGameOuts: number = 0,
): {
  eligible: boolean;
  reason?: string;
  warningReason?: string;
  pitchesRemaining: number;
  dailyMax: number;
  eventMax?: number;
  totalPitchesInEvent?: number;
  pitchesRemainingInEvent?: number;
  inningsToday?: number;
  inningsInEvent?: number;
  outsToday?: number;
  outsInRollingWindow?: number;
  restStatus?: {
    lastAppearance: PitchingLogEntry;
    eligibleDate: string;
    daysRemaining: number;
  };
} {
  const ageRules = (rules.id === 'custom' && customRules)
    ? customRules
    : (rules.ageGroups[ageGroup] || rules.ageGroups['all'] || rules.ageGroups['12U'] || { logicType: 'threshold', dailyMax: 85, enforceEventMax: false, restThresholds: [] });

  const logicType = ageRules.logicType || 'threshold';
  const gameDateStr = dateOnly(gameDate);

  // ── Innings-based logic (USSSA) ───────────────────────────────────────────
  if (logicType === 'innings') {
    const relevantLog = enforceGlobalRest ? pitchingLog : pitchingLog.filter(l => l.tournamentId === currentTournamentId);

    // Event cap — only meaningful INSIDE a tournament. Outside one,
    // `currentTournamentId` is undefined and unscoped regular-season entries (also
    // undefined tournamentId) would otherwise accumulate across the whole season
    // and falsely trip the event max. Guard so the event cap engages only when
    // this game actually belongs to a tournament.
    const inningsInEvent = currentTournamentId
      ? relevantLog
          .filter(e => e.tournamentId === currentTournamentId)
          .reduce((s, e) => s + (e.inningsPitched ?? 0), 0)
      : 0;
    const totalInningsInEvent = inningsInEvent + currentGameInnings;

    if (currentTournamentId && ageRules.eventMaxInnings && totalInningsInEvent >= ageRules.eventMaxInnings) {
      return {
        eligible: false,
        reason: `Reached tournament max of ${ageRules.eventMaxInnings} innings.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        inningsToday: 0,
        inningsInEvent,
      };
    }

    // Daily cap
    const inningsToday = relevantLog
      .filter(e => e.date.split('T')[0] === gameDateStr)
      .reduce((s, e) => s + (e.inningsPitched ?? 0), 0);
    const totalInningsToday = inningsToday + currentGameInnings;

    if (ageRules.dailyMaxInnings && totalInningsToday >= ageRules.dailyMaxInnings) {
      return {
        eligible: false,
        reason: `Reached daily max of ${ageRules.dailyMaxInnings} innings.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        inningsToday,
        inningsInEvent,
      };
    }

    // Next-day block: if yesterday's innings >= nextDayBlockInnings → blocked today
    const yesterday = new Date(gameDate);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = dateOnly(yesterday);
    const yesterdayInnings = relevantLog
      .filter(e => e.date.split('T')[0] === yesterdayStr)
      .reduce((s, e) => s + (e.inningsPitched ?? 0), 0);

    if (ageRules.nextDayBlockInnings && yesterdayInnings >= ageRules.nextDayBlockInnings) {
      return {
        eligible: false,
        reason: `Threw ${yesterdayInnings} innings yesterday, which requires a full day of rest.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        inningsToday,
        inningsInEvent,
      };
    }

    return {
      eligible: true,
      pitchesRemaining: 999,
      dailyMax: ageRules.dailyMax,
      inningsToday,
      inningsInEvent,
    };
  }

  // ── Outs-based logic (Perfect Game) ──────────────────────────────────────
  if (logicType === 'outs') {
    const windowDays = ageRules.rollingOutsWindowDays ?? 3;
    const windowStart = new Date(gameDate);
    windowStart.setDate(windowStart.getDate() - (windowDays - 1));
    const windowStartStr = dateOnly(windowStart);

    const relevantLog = enforceGlobalRest ? pitchingLog : pitchingLog.filter(l => l.tournamentId === currentTournamentId);

    const outsInWindow = relevantLog
      .filter(e => e.date.split('T')[0] >= windowStartStr && e.date.split('T')[0] <= gameDateStr)
      .reduce((s, e) => s + (e.outsPitched ?? 0), 0);
    const totalOutsInWindow = outsInWindow + currentGameOuts;

    if (ageRules.rollingOutsWindowMax && totalOutsInWindow >= ageRules.rollingOutsWindowMax) {
      return {
        eligible: false,
        reason: `Reached ${windowDays}-day rolling max of ${ageRules.rollingOutsWindowMax} outs.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        outsToday: 0,
        outsInRollingWindow: outsInWindow,
      };
    }

    const outsToday = relevantLog
      .filter(e => e.date.split('T')[0] === gameDateStr)
      .reduce((s, e) => s + (e.outsPitched ?? 0), 0);
    const totalOutsToday = outsToday + currentGameOuts;

    if (ageRules.dailyMaxOuts && totalOutsToday >= ageRules.dailyMaxOuts) {
      return {
        eligible: false,
        reason: `Reached daily max of ${ageRules.dailyMaxOuts} outs.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        outsToday,
        outsInRollingWindow: outsInWindow,
      };
    }

    const pitchesToday = relevantLog
      .filter(e => e.date.split('T')[0] === gameDateStr)
      .reduce((s, e) => s + e.pitchCount, 0);
    const totalPitchesToday = pitchesToday + currentGamePitchCount;
    const pitchCap = ageRules.dailyMaxPitches ?? ageRules.dailyMax;

    if (totalPitchesToday >= pitchCap) {
      return {
        eligible: false,
        reason: `Reached daily pitch limit of ${pitchCap}.`,
        pitchesRemaining: 0,
        dailyMax: ageRules.dailyMax,
        outsToday,
        outsInRollingWindow: outsInWindow,
      };
    }

    return {
      eligible: true,
      pitchesRemaining: Math.max(0, pitchCap - totalPitchesToday),
      dailyMax: ageRules.dailyMax,
      outsToday,
      outsInRollingWindow: outsInWindow,
    };
  }

  // ── Threshold-based logic (Little League, NFHS, custom) ──────────────────
  const dailyMax = ageRules.dailyMax;
  const eventMax = ageRules.eventMax;
  const restThresholds: RestThreshold[] = ageRules.restThresholds || [];

  const gameDateOnly = new Date(gameDate);
  gameDateOnly.setHours(0, 0, 0, 0);

  const relevantLogsForRest = enforceGlobalRest
    ? pitchingLog
    : pitchingLog.filter(l => l.tournamentId === currentTournamentId);

  // Each log entry carries a frozen `eligibleDate` computed under whatever rules
  // were active when that outing was SAVED. When the CURRENT ruleset imposes no
  // rest (empty restThresholds — e.g. a Custom tournament's "No Rest Days
  // Required"), that stale stored date must be ignored, or a pitcher stays
  // wrongly blocked. So only consult stored eligibleDates when rest can apply.
  const activeRestEntry = restThresholds.length > 0
    ? relevantLogsForRest.find(entry => {
        const eligibleDate = new Date(entry.eligibleDate);
        eligibleDate.setHours(0, 0, 0, 0);
        return eligibleDate.getTime() > gameDateOnly.getTime();
      })
    : undefined;

  if (activeRestEntry) {
    const eligibleDate = new Date(activeRestEntry.eligibleDate);
    const diffTime = eligibleDate.getTime() - gameDateOnly.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return {
      eligible: false,
      reason: `Threw ${activeRestEntry.pitchCount} pitches on ${new Date(activeRestEntry.date).toLocaleDateString()}, requires ${activeRestEntry.requiredRestDays} rest days.`,
      pitchesRemaining: 0,
      dailyMax,
      restStatus: {
        lastAppearance: activeRestEntry,
        eligibleDate: activeRestEntry.eligibleDate,
        daysRemaining: diffDays,
      },
    };
  }

  // Event max (used by orgs that add a tournament pitch cap)
  let totalPitchesInEvent = 0;
  let pitchesRemainingInEvent: number | undefined = undefined;

  if (currentTournamentId && eventMax) {
    totalPitchesInEvent = pitchingLog
      .filter(e => e.tournamentId === currentTournamentId)
      .reduce((s, e) => s + e.pitchCount, 0);
    pitchesRemainingInEvent = Math.max(0, eventMax - (totalPitchesInEvent + currentGamePitchCount));

    if (ageRules.enforceEventMax && (totalPitchesInEvent + currentGamePitchCount) >= eventMax) {
      return {
        eligible: false,
        reason: `Reached tournament max of ${eventMax} pitches.`,
        pitchesRemaining: 0,
        dailyMax,
        eventMax,
        totalPitchesInEvent,
        pitchesRemainingInEvent: 0,
      };
    }
  }

  // Daily max
  const todayPitches = pitchingLog
    .filter(e => new Date(e.date).toDateString() === gameDate.toDateString())
    .reduce((s, e) => s + e.pitchCount, 0);
  const totalToday = todayPitches + currentGamePitchCount;
  const pitchesRemaining = Math.max(0, dailyMax - totalToday);

  if (totalToday >= dailyMax) {
    return {
      eligible: false,
      reason: `Reached daily max of ${dailyMax} pitches.`,
      pitchesRemaining: 0,
      dailyMax,
      eventMax,
      totalPitchesInEvent,
      pitchesRemainingInEvent,
    };
  }

  // NFHS advisory checks (non-blocking)
  let warningReason: string | undefined;
  if (ageRules.rollingWindowDays && ageRules.rollingWindowMaxPitches) {
    const windowStart = new Date(gameDate);
    windowStart.setDate(windowStart.getDate() - (ageRules.rollingWindowDays - 1));
    const windowStartStr = dateOnly(windowStart);
    const windowPitches = pitchingLog
      .filter(e => e.date.split('T')[0] >= windowStartStr)
      .reduce((s, e) => s + e.pitchCount, 0);
    if (windowPitches + currentGamePitchCount >= ageRules.rollingWindowMaxPitches) {
      warningReason = `Approaching ${ageRules.rollingWindowMaxPitches}-pitch ${ageRules.rollingWindowDays}-day window`;
    }
  }
  if (!warningReason && ageRules.noConsecutiveDays) {
    const d1 = new Date(gameDate); d1.setDate(d1.getDate() - 1);
    const d2 = new Date(gameDate); d2.setDate(d2.getDate() - 2);
    const d1Str = dateOnly(d1);
    const d2Str = dateOnly(d2);
    const pitchedD1 = pitchingLog.some(e => e.date.split('T')[0] === d1Str && e.pitchCount > 0);
    const pitchedD2 = pitchingLog.some(e => e.date.split('T')[0] === d2Str && e.pitchCount > 0);
    if (pitchedD1 && pitchedD2) {
      warningReason = '3 consecutive days pitching (NFHS advisory)';
    }
  }

  const isEligible = totalToday < dailyMax && (!ageRules.enforceEventMax || !eventMax || (totalPitchesInEvent + currentGamePitchCount) < eventMax);

  return {
    eligible: isEligible,
    warningReason,
    pitchesRemaining: Math.min(pitchesRemaining, pitchesRemainingInEvent !== undefined ? pitchesRemainingInEvent : pitchesRemaining),
    dailyMax,
    eventMax,
    totalPitchesInEvent,
    pitchesRemainingInEvent,
  };
}
